import { Injectable } from "@angular/core";
import { ProductType } from "@obg/ngx.core";
import { memoizeSelector } from "@obg/ngx.global";
import { isEmpty } from "lodash-es";

import { CommonState } from "../../state";
import { CommonContentType } from "../common-content.model";
import { CommonContentSelector } from "../common-content.selector";
import { getContentKey } from "../common-content.utils";

import { Banner } from "./banner.model";
import { filterByGroup, getAllGroups } from "./banner.utils";

// @dynamic
@Injectable({
	providedIn: "root",
})
export class BannerSelector extends CommonContentSelector {
	getMainBanners() {
		return super.getByParams({
			presenceType: ProductType.Common,
			contentType: CommonContentType.Main,
		});
	}

	@memoizeSelector<CommonState>((state) => [state.commonContent.items, state.auth?.isAuthenticated])
	getPromotionGroups() {
		const bannersKey = getContentKey({
			presenceType: ProductType.Common,
			contentType: CommonContentType.Promotional,
		});
		return (state: CommonState): string[] => getAllGroups(state.commonContent.items[bannersKey]);
	}

	@memoizeSelector<CommonState>((state) => [state.commonContent.items, state.auth?.isAuthenticated])
	getPromotionBanners(groups: string[], isHighlighted?: boolean, productType?: ProductType) {
		const bannerParams = {
			presenceType: ProductType.Common,
			contentType: CommonContentType.Promotional,
		};

		return (state: CommonState): Banner[] | undefined => {
			const bannersKey = getContentKey(bannerParams);
			if (!state.commonContent.items[bannersKey]) {
				return undefined;
			}
			let banners = super.getByParams(bannerParams)(state) as Banner[];

			if (!isEmpty(groups)) {
				banners = filterByGroup(banners, groups);
			}

			if (isHighlighted) {
				banners = banners.filter((banner) => banner.highlighted);
			}

			if (productType) {
				banners = banners.filter((banner) => banner.productType === productType);
			}

			return banners;
		};
	}

	getSponsorshipBanner() {
		const bannerParams = {
			presenceType: ProductType.Sponsorship,
			contentType: CommonContentType.Banner,
		};

		return (state: CommonState): Banner[] | undefined => {
			const bannersKey = getContentKey(bannerParams);
			if (!state.commonContent.items[bannersKey]) {
				return undefined;
			}
			const banners = super.getByParams(bannerParams)(state) as Banner[];
			return banners;
		};
	}
}
